import styled from "styled-components"
import React, { useEffect, useState } from "react"
import { useSelector, useDispatch } from "react-redux"

import { IDLE, PENDING, ADD, EDIT, OPEN } from "const"
import { OkrObjective, OkrKeyResult } from "server/models"
import { StateType, hasAccess } from "client/utils"
import {
    PRM_OKR_OBJECTIVE_KEY_RESULT,
    StoreType,
    AccountUserList,
    OkrObjectiveFilters,
    OkrKeyResultUpdateList,

    LoadAccountUsers,
    LoadOkrMetrics,
    LoadAllOkrObjectives,
    LoadAllOkrKeyResults,
    LoadOkrKeyResultUpdates,
    SortOkrObjectives,

    setAccountUserList,
    setOkrMetricList,
    setOkrMetricIds,
    setOkrObjectiveList,
    setOkrObjectiveIds,
    setOkrKeyResultList,
    setOkrKeyResultIds,
    setOkrKeyResultUpdateList,
    setOkrObjectiveSort,
    setOkrObjectiveFilters,
    setOkrObjectiveActive,
    addModal,
} from "client/store"
import {
    Container,
    PageContent,
    Loader,
    Button,
    Table,
    TableSearchWrapper,
    SortRows,
} from "client/components"

import OkrObjectiveSearch from "client/pages/search/okrObjectiveSearch"
import Columns from "./columns"
import PopupMenu from "./popupMenu"
import ConfirmDelete from "./confirmDelete"

const TableStyled = styled(Table)`
    tr tr { // styles for nested table
        background: transparent;

        &:first-child > td {
            border-top: none;
        }
    }

    tr:hover tr td {
        border-color: ${(props) => props.theme.keyResultTableRowBorderHoverColor};
    }
`

interface ListAllProps {
    dataLoadedCallback: () => void
}

const ListAll = ({
    dataLoadedCallback,
}: ListAllProps) => {
    const session = useSelector((state: StoreType) => state.okrSession.sessionView)

    const objectiveBaseRoute = useSelector((state: StoreType) => state.okrObjective.baseRoute)
    const objectiveRoute = useSelector((state: StoreType) => state.okrObjective.objectiveRoute)
    const objectiveList = useSelector((state: StoreType) => state.okrObjective.objectiveList)
    const objectiveIds = useSelector((state: StoreType) => state.okrObjective.objectiveIds)

    const keyResultList = useSelector((state: StoreType) => state.okrKeyResult.keyResultList)
    const keyResultIds = useSelector((state: StoreType) => state.okrKeyResult.keyResultIds)

    const sortColumn = useSelector((state: StoreType) => state.okrObjective.sortColumn)
    const sortOrder = useSelector((state: StoreType) => state.okrObjective.sortOrder)
    const filters = useSelector((state: StoreType) => state.okrObjective.filters)
    const reloadList = useSelector((state: StoreType) => state.okrObjective.reloadList)

    // reload metric list when adding new metric from KR edit page
    const reloadMetricList = useSelector((state: StoreType) => state.okrMetric.reloadList)

    const userData = useSelector((state: StoreType) => state.system.userData)
    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()

    const [loadDataState, setLoadDataState] = useState<StateType>(IDLE)
    const [loadMetricState, setLoadMetricState] = useState<StateType>(IDLE)
    const [loadState, setLoadState] = useState<StateType>(IDLE)

    useEffect(() => {
        setLoadDataState(PENDING)
        // account user list for search and owner assign
        LoadAccountUsers(userData.accountId).then((res) => {
            const accountUserList: AccountUserList = { [userData.accountId]: res }
            dispatch(setAccountUserList(accountUserList))
        }).then(() => {
            setLoadDataState(IDLE)
        })

        return () => {
            // clear popup menu object
            dispatch(setOkrObjectiveActive(null))
        }
    }, [])

    useEffect(() => {
        setLoadMetricState(PENDING)
        LoadOkrMetrics(userData.accountId).then((res) => {
            dispatch(setOkrMetricList(res.metricList))
            dispatch(setOkrMetricIds(res.metricIds))
        }).then(() => {
            setLoadMetricState(IDLE)
        })
    }, [reloadMetricList])

    useEffect(() => {
        if (!reloadList) return
        setLoadState(PENDING)
        Promise.all([
            LoadAllOkrObjectives(session.id).then((res) => {
                dispatch(setOkrObjectiveList(res.objectiveList))
            }),
            LoadAllOkrKeyResults(session.id).then((res) => {
                dispatch(setOkrKeyResultList(res.keyResultList))
                dispatch(setOkrKeyResultIds(res.keyResultIds))

                const updateList: OkrKeyResultUpdateList = {}
                const promiseArray: Promise<any>[] = []

                Object.values(res.keyResultList).forEach((keyResult: OkrKeyResult) => {
                    promiseArray.push(
                        LoadOkrKeyResultUpdates(keyResult).then((updates) => {
                            updateList[keyResult.id] = updates
                        }),
                    )
                })
                return Promise.all(promiseArray).then(() => {
                    dispatch(setOkrKeyResultUpdateList(updateList))
                })
            }),
        ]).then(() => {
            setLoadState(IDLE)
            dataLoadedCallback()
        })
    }, [session, reloadList])

    useEffect(() => {
        const sortedIds = SortOkrObjectives(Object.values(objectiveList), keyResultList, keyResultIds, sortColumn, sortOrder, filters)
        dispatch(setOkrObjectiveIds(sortedIds))
    }, [objectiveList, keyResultList, keyResultIds, sortColumn, sortOrder, filters])

    const onSort: SortRows = (column: keyof OkrObjective, order) => {
        dispatch(setOkrObjectiveSort(column, order))
    }

    const onFilter = (newFilters: OkrObjectiveFilters) => {
        dispatch(setOkrObjectiveFilters(newFilters))
    }

    const columns = Columns()

    const isOwnerSession = session.owner === userData.userId
    const isOpenSession = session.status === OPEN

    const addAccess = hasAccess(PRM_OKR_OBJECTIVE_KEY_RESULT, ADD)
    const allowedToAdd = (addAccess || isOwnerSession) && isOpenSession

    return (
        <Container>
            <PageContent>
                {
                    (
                        loadDataState === PENDING
                        || loadMetricState === PENDING
                        || loadState === PENDING
                    ) && <Loader />
                }
                <TableSearchWrapper>
                    <OkrObjectiveSearch
                        id="okrObjectiveList"
                        rightOffset={allowedToAdd}
                        filters={filters}
                        onFilter={onFilter}
                    />
                    {
                        allowedToAdd && (
                            <Button
                                variant="success"
                                onClick={() => dispatch(addModal({
                                    url: `${objectiveBaseRoute}${objectiveRoute}/${EDIT}/0`,
                                }))}
                            >
                                {lang.add}
                            </Button>
                        )
                    }
                </TableSearchWrapper>
                <TableStyled
                    columns={columns}
                    dataList={objectiveList}
                    dataIds={objectiveIds}
                    sortColumn={sortColumn}
                    sortOrder={sortOrder}
                    rowSize="fit"
                    showPagination
                    onSort={onSort}
                />

                <PopupMenu />
                <ConfirmDelete />
            </PageContent>
        </Container>
    )
}
export default ListAll
